import React, { useEffect, useState } from 'react';
import { Analytics } from '@vercel/analytics/react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Topbar from './components/Topbar';
import { supabase } from './lib/supabase';
import AthletesPage from './pages/AthletesPage';
import BrandPage from './pages/BrandPage';
import InstitutionsDeckPage from './pages/InstitutionsDeckPage';
import MonarchsDeckPage from './pages/MonarchsDeckPage';
import HbcubaPage from './pages/HbcubaPage';
import Raptors905DeckPage from './pages/Raptors905DeckPage';
import DeckPage from './pages/DeckPage';
import DashboardPage from './pages/DashboardPage';
import DataRequestPage from './pages/DataRequestPage';
import HuddlePage from './pages/HuddlePage';
import InstitutionDashboardPage from './pages/InstitutionDashboardPage';
import InstitutionsPage from './pages/InstitutionsPage';
import InsightsPage from './pages/InsightsPage';
import LandingPage from './pages/LandingPage';
import ProgramsPage from './pages/ProgramsPage';
import RequestAccessPage from './pages/RequestAccessPage';
import LoginPage from './pages/LoginPage';
import NotFoundPage from './pages/NotFoundPage';
import MentorsPage from './pages/MentorsPage';
import InvoicePage from './pages/InvoicePage';
import ReportsPage from './pages/ReportsPage';
import StudentLoginPage from './pages/StudentLoginPage';
import PartnerLoginPage from './pages/PartnerLoginPage';
import StudentPortalShell from './pages/StudentPortalShell';
import EarlyPathwaysSignupPage from './pages/EarlyPathwaysSignupPage';
import EarlyPathwaysLoginPage from './pages/EarlyPathwaysLoginPage';
import EarlyPathwaysPortalPage from './pages/EarlyPathwaysPortalPage';
import WaitlistPage from './pages/WaitlistPage';
import { AdminProfile, NavPage } from './types';

const PLATFORM_TYPES = ['individual', 'bthd_internal', null, undefined, ''];

function AdminShell() {
  const [activePage, setActivePage] = React.useState<NavPage>('dashboard');
  const [adminProfile, setAdminProfile] = useState<AdminProfile | null>(null);

  useEffect(() => {
    supabase.auth.getUser().then(async ({ data: { user } }) => {
      if (!user) return;
      const { data } = await supabase
        .from('profiles')
        .select('institution_type, institution_id, is_admin, full_name')
        .eq('id', user.id)
        .single();
      if (data) {
        let institutionName: string | null = null;
        if (data.institution_id) {
          const { data: inst } = await supabase
            .from('institutions')
            .select('name')
            .eq('id', data.institution_id)
            .single();
          institutionName = inst?.name ?? null;
        }
        setAdminProfile({
          fullName: data.full_name ?? null,
          institutionType: data.institution_type ?? null,
          institutionId: data.institution_id ?? null,
          institutionName,
          isAdmin: data.is_admin ?? false,
        });
      }
    });
  }, []);

  const isOrgAdmin = adminProfile?.isAdmin &&
    !PLATFORM_TYPES.includes(adminProfile?.institutionType as any);

  const renderPage = () => {
    // Org admins see their institution dashboard instead of the global dashboard
    if (isOrgAdmin && activePage === 'dashboard' && adminProfile?.institutionType) {
      return (
        <InstitutionDashboardPage
          institutionType={adminProfile.institutionType}
          institutionId={adminProfile.institutionId}
          institutionName={adminProfile.institutionName ?? undefined}
        />
      );
    }
    switch (activePage) {
      case 'dashboard':    return <DashboardPage />;
      case 'athletes':     return <AthletesPage institutionId={isOrgAdmin ? adminProfile?.institutionId : null} />;
      case 'institutions': return <InstitutionsPage />;
      case 'mentors':      return <MentorsPage />;
      case 'huddle':       return <HuddlePage />;
      case 'waitlist':     return <WaitlistPage />;
      case 'insights':     return <InsightsPage institutionId={isOrgAdmin ? adminProfile?.institutionId ?? null : null} />;
      case 'reports':      return <ReportsPage />;
      case 'pathways':
      case 'legacy':
      case 'leadership':
      case 'settings':
      default:             return <DashboardPage />;
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
  };

  return (
    <div className="app-shell">
      <Sidebar activePage={activePage} onNavigate={(page) => setActivePage(page)} onLogout={handleLogout} adminProfile={adminProfile} />
      <div className="main-area">
        <Topbar activePage={activePage} adminProfile={adminProfile} />
        <main className="page-content">
          {renderPage()}
        </main>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });
    return () => subscription.unsubscribe();
  }, []);

  if (loading) return <div style={{ minHeight: '100vh', background: '#0a0f1a' }} />;
  if (!session) return <Navigate to="/admin/login" replace />;
  return <>{children}</>;
}

function ProtectedStudentRoute({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });
    return () => subscription.unsubscribe();
  }, []);

  if (loading) return <div style={{ minHeight: '100vh', background: '#0a0f1a' }} />;
  if (!session) return <Navigate to="/athlete/login" replace />;
  return <>{children}</>;
}

function ProtectedEarlyPathwaysRoute({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });
    return () => subscription.unsubscribe();
  }, []);

  if (loading) return <div style={{ minHeight: '100vh', background: '#0a0f1a' }} />;
  if (!session) return <Navigate to="/early-pathways/login" replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/data-request" element={<DataRequestPage />} />
        <Route path="/programs" element={<ProgramsPage />} />
        <Route path="/programs/request-access" element={<RequestAccessPage />} />
        <Route path="/brand" element={<BrandPage />} />
        <Route path="/deck" element={<DeckPage />} />
        <Route path="/institutions" element={<InstitutionsDeckPage />} />
        <Route path="/cibc" element={<Navigate to="/institutions" replace />} />
        <Route path="/905" element={<Raptors905DeckPage />} />
        <Route path="/monarchs" element={<MonarchsDeckPage />} />
        <Route path="/hbcuba" element={<HbcubaPage />} />
        <Route path="/admin/login" element={<LoginPage />} />
        <Route path="/admin" element={
          <ProtectedRoute>
            <AdminShell />
          </ProtectedRoute>
        } />
        {/* Athlete Portal — canonical URLs */}
        <Route path="/athlete/login" element={<StudentLoginPage />} />
        <Route path="/athlete" element={
          <ProtectedStudentRoute>
            <StudentPortalShell />
          </ProtectedStudentRoute>
        } />
        {/* Legacy redirects — /student → /athlete */}
        <Route path="/student/login" element={<Navigate to="/athlete/login" replace />} />
        <Route path="/student" element={<Navigate to="/athlete" replace />} />
        <Route path="/partner/login" element={<PartnerLoginPage />} />
        {/* Early Pathways Portal */}
        <Route path="/early-pathways/signup" element={<EarlyPathwaysSignupPage />} />
        <Route path="/early-pathways/login" element={<EarlyPathwaysLoginPage />} />
        <Route path="/early-pathways" element={
          <ProtectedEarlyPathwaysRoute>
            <EarlyPathwaysPortalPage />
          </ProtectedEarlyPathwaysRoute>
        } />
        <Route path="/invoice" element={
          <ProtectedRoute>
            <InvoicePage />
          </ProtectedRoute>
        } />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <Analytics />
    </BrowserRouter>
  );
}
